import { useEffect, useRef } from 'react';

import styles from './CommandFSearch.module.scss';
import { COMMAND_F_SEARCH_ID, HiddenSearchableTextSpan } from './HiddenSearchableTextSpan';

/**
 * To extend the browser's native "find" or "command + f" search functionality, we can use the
 * `useCommandFSearch` hook with the related components and styles. We can extend this "find"
 * functionality by highlighting and scrolling into view and entire element that the user is
 * searching for, rather than just a piece of text.
 *
 * `useCommandFSearch` should be invoked in the views where it is expected to be used (e.g.
 * `pages/SectionPageEditPage`). This way, the functionality can be initiated without creating
 * duplicate event listeners.
 *
 * There are two other steps that need to be taken:
 *   1. Identify the elements that the user will be able to search for.
 *   2. Identify what text the user can use to find it (e.g. an ID).
 *
 * Once the searchable element has been identified the `commandFStyles.commandFSearchContainer`
 * style should be added to that element. Without this added style the hook will not work. After
 * adding the style to the parent, the `HiddenSearchableTextSpan` component should be added as a
 * direct child of the component with the `commandFStyles.commandFSearchContainer` style. Both
 * `commandFStyles` and `HiddenSearchableTextSpan` are exported from `useCommandFSearch`.
 *
 * After everything is correctly set up, a user should be able to press "command + f", enter in the
 * term they wish to search for, and when there is a match, the element that contains the match
 * scroll it into view if it is not already in view, and the element itself should be highlighted
 * in yellow for 2 seconds.
 */
export function useCommandFSearch() {
  const hiddenSearchablTextSpanRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // If the current selection matches text within `HiddenSearchableTextSpan` scroll it into view
    // and highlight it yellow for 2 seconds.
    const handleCommandFSearch = () => {
      const selectedClass = styles.commandFSearchContainerSelected;

      // if we have previously selected a different element, clear that selection
      if (hiddenSearchablTextSpanRef.current?.parentElement) {
        hiddenSearchablTextSpanRef.current.parentElement.classList.remove(selectedClass);
      }

      // `focusedText` is the text that is matched itself, its parent may be the
      // `HiddenSearchableTextSpan` component.
      const focusedText = window.getSelection()?.focusNode;
      hiddenSearchablTextSpanRef.current = focusedText?.parentElement ?? null;

      // The current selection is not text that we wish to handle using this hook
      if (!hiddenSearchablTextSpanRef.current || hiddenSearchablTextSpanRef.current.id !== COMMAND_F_SEARCH_ID) return;

      hiddenSearchablTextSpanRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      let currentNode = hiddenSearchablTextSpanRef.current.parentElement;

      while (currentNode) {
        if (currentNode.classList.contains(styles.commandFSearchContainer)) break;
        else currentNode = currentNode.parentElement;
      }

      currentNode?.classList.add(selectedClass);
      setTimeout(() => {
        currentNode?.classList.remove(selectedClass);
      }, 2000);
    };

    document.addEventListener('selectionchange', handleCommandFSearch);

    return () => {
      document.removeEventListener('selectionchange', handleCommandFSearch);
    };
  }, []);
}

export { HiddenSearchableTextSpan, styles as commandFSearchStyles };
